<template>
    <div class="mt-8">
        <v-data-table
            :headers="headers"
            :items="items"
            class="custom-locations-table elevation-0"
            :loading="loading"
            disable-pagination
            hide-default-footer
        >
            <template v-slot:top>
                <div class="top">
                    <v-row
                        no-gutter
                        class="mt-0 mx-0 pt-4 pb-0 pb-4"
                        :style="{ 'background-color': '#eeeeee' }"
                    >
                        <h1 class="mr-4 ml-4">CUSTOM LOCATIONS</h1>
                        <v-spacer />
                        <v-btn
                            color="primary"
                            class="d-flex align-center mr-4"
                            rounded
                            :disabled="!editSettingPermission || loading"
                            @click="openCreateDialog"
                        >
                            ADD LOCATION
                        </v-btn>
                    </v-row>
                </div>
            </template>
            <template v-slot:no-data>
                <div class="text-center py-6 grey--text text--darken-1">
                    No custom locations yet. Use ADD LOCATION to create one.
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openEditDialog(item)">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                        small
                        icon
                        :disabled="!editSettingPermission"
                        @click="openDeleteItem(item)"
                    >
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <v-dialog
            v-model="openDialog"
            max-width="700px"
            persistent
            :retain-focus="false"
        >
            <CustomLocationForm
                v-if="openDialog"
                :setting="setting"
                :create-form="createForm"
                :original-location="selectedItem"
                :edit-setting-permission="editSettingPermission"
                :existing-locations="items"
                @close="closeDialog"
                @saved="onSaved"
                @updated="onUpdated"
            />
        </v-dialog>
        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5">Delete Item</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this custom location?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteItem">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteItem"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar v-model="snackbar" color="primary" timeout="8000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
export default {
    name: 'CustomLocationsSection',
    components: {
        CustomLocationForm: () => import('./CustomLocationForm.vue'),
    },
    props: {
        setting: {
            type: Object,
            required: true,
        },
        editSettingPermission: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        loading: false,
        items: [],
        openDialog: false,
        openDeleteWarning: false,
        createForm: true,
        selectedItem: {},
        snackbar: false,
        snackbarMessage: '',
        headers: [
            {
                text: 'COUNTRY',
                value: 'country',
                sortable: false,
                align: 'center',
            },
            { text: 'STATE', value: 'state', sortable: false, align: 'center' },
            { text: 'CITY', value: 'city', sortable: false, align: 'center' },
            {
                text: 'ACTIONS',
                value: 'actions',
                sortable: false,
                align: 'center',
            },
        ],
    }),
    async mounted() {
        await this.loadItems()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async loadItems() {
            try {
                this.loading = true
                this.items = await API.getCustomLocations(this.setting.id)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        openCreateDialog() {
            this.createForm = true
            this.selectedItem = {}
            this.openDialog = true
        },
        openEditDialog(item) {
            this.createForm = false
            this.selectedItem = { ...item }
            this.openDialog = true
        },
        closeDialog() {
            this.openDialog = false
            this.selectedItem = {}
        },
        showSavedAlert() {
            this.snackbarMessage =
                'Location saved. Add a shipping cost above for this country, state, and city. Until a cost exists, clients can select this location but quotes will go to Approval.'
            this.snackbar = true
        },
        async onSaved(customLocation) {
            this.items = [...this.items, customLocation].sort((a, b) => {
                const countryCompare = String(a.country || '').localeCompare(
                    String(b.country || ''),
                    undefined,
                    {
                        sensitivity: 'base',
                    }
                )
                if (countryCompare !== 0) {
                    return countryCompare
                }
                const stateCompare = String(a.state || '').localeCompare(
                    String(b.state || ''),
                    undefined,
                    { sensitivity: 'base' }
                )
                if (stateCompare !== 0) {
                    return stateCompare
                }
                return String(a.city || '').localeCompare(
                    String(b.city || ''),
                    undefined,
                    { sensitivity: 'base' }
                )
            })
            this.showSavedAlert()
        },
        async onUpdated(customLocation) {
            const index = this.items.findIndex(
                item => item.id === customLocation.id
            )
            if (index >= 0) {
                this.items.splice(index, 1, customLocation)
                this.items = [...this.items].sort((a, b) => {
                    const countryCompare = String(
                        a.country || ''
                    ).localeCompare(String(b.country || ''), undefined, {
                        sensitivity: 'base',
                    })
                    if (countryCompare !== 0) {
                        return countryCompare
                    }
                    const stateCompare = String(a.state || '').localeCompare(
                        String(b.state || ''),
                        undefined,
                        {
                            sensitivity: 'base',
                        }
                    )
                    if (stateCompare !== 0) {
                        return stateCompare
                    }
                    return String(a.city || '').localeCompare(
                        String(b.city || ''),
                        undefined,
                        { sensitivity: 'base' }
                    )
                })
            }
        },
        openDeleteItem(item) {
            this.selectedItem = { ...item }
            this.openDeleteWarning = true
        },
        closeDeleteItem() {
            this.selectedItem = {}
            this.openDeleteWarning = false
        },
        async deleteItem() {
            try {
                this.loading = true
                await API.deleteCustomLocation({
                    settingId: this.setting.id,
                    id: this.selectedItem.id,
                })
                this.items = this.items.filter(
                    entry => entry.id !== this.selectedItem.id
                )
                this.closeDeleteItem()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.custom-locations-table >>> thead th {
    background-color: #eeeeee !important;
    font-weight: 700 !important;
}
</style>
